import { useMemo } from 'react';

import { AscProject } from '@neo.tax/asc/hooks/useAscProjects';
import { AscContributorProjectAllocation, ContributorAllocationsInPeriod } from '@neo.tax/asc/types';
import { CapitalizationStatus } from '@neo.tax/shared';

interface UseFilteredProjectAllocationsParams {
  modalAllocationPercents: ContributorAllocationsInPeriod;
  filteredAscContributorProjectAllocations: AscContributorProjectAllocation[] | null;
  selectedAscProject?: AscProject;
}

export interface ProjectAllocation {
  projectId: string;
  projectName: string;
  allocationPercent: number;
  originalAllocationPercent: number;
  lastAllocationPercentOnProjectPercentUpdate: number;
  projectMonthsPerContributor: any[];
  capitalizationStatus: CapitalizationStatus;
  capitalizationStartDate: Date | null;
  capitalizationEndDate: Date | null;
}

interface UseFilteredProjectAllocationsReturn {
  projectAllocations: ProjectAllocation[];
  selectedProjectAllocation?: ProjectAllocation;
}

/**
 * Filters and sorts project allocations for display in the modal.
 *
 * Handles:
 * - Filtering allocations based on search results
 * - Excluding the selected project from the main list (it's rendered separately)
 * - Extracting the selected project's allocation details
 */
export const useFilteredProjectAllocations = ({
  filteredAscContributorProjectAllocations,
  modalAllocationPercents,
  selectedAscProject,
}: UseFilteredProjectAllocationsParams): UseFilteredProjectAllocationsReturn => {
  // Create set of project IDs that match the current search
  const filteredProjectIds = useMemo(
    () => new Set(filteredAscContributorProjectAllocations?.map((allocation) => allocation.ascProjectId) ?? []),
    [filteredAscContributorProjectAllocations],
  );

  const projectAllocations = useMemo(
    () =>
      [...modalAllocationPercents.projectPercents.projectIdToProjectAllocations.values()].filter(
        (projectAllocation) =>
          // Exclude the selected project as it's rendered separately
          projectAllocation.projectId !== selectedAscProject?.id &&
          (filteredProjectIds.size === 0 || filteredProjectIds.has(projectAllocation.projectId)),
      ),
    [modalAllocationPercents, filteredProjectIds, selectedAscProject?.id],
  );

  const selectedProjectAllocation = useMemo(
    () =>
      [...modalAllocationPercents.projectPercents.projectIdToProjectAllocations.values()]
        .filter((projectAllocation) => projectAllocation.projectId === selectedAscProject?.id)
        .pop(),
    [modalAllocationPercents, selectedAscProject?.id],
  );

  return {
    projectAllocations,
    selectedProjectAllocation,
  };
};
